import { useState, useEffect } from 'react'
import { Brain } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { useLanguage } from '../contexts/LanguageContext'
import { supabase } from '../lib/supabase'
import SimpleTracking from '../components/beta/SimpleTracking'

export default function BetaPageDebug() {
  const { t } = useLanguage()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [debugInfo, setDebugInfo] = useState<any>({})

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      const authenticated = !!user
      setIsAuthenticated(authenticated)
      setDebugInfo({
        userExists: !!user,
        userEmail: user?.email || 'not logged in',
        timestamp: new Date().toISOString()
      })
    }
    checkAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      const authenticated = !!session?.user
      setIsAuthenticated(authenticated)
      setDebugInfo((prev: any) => ({
        ...prev,
        sessionExists: !!session,
        userEmail: session?.user?.email || 'not logged in',
        lastUpdate: new Date().toISOString()
      }))
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950">
      {/* Debug Info */}
      <div className="bg-red-100 border-2 border-red-500 p-4 m-4">
        <h2 className="font-bold text-red-800">🐛 DEBUG INFO</h2>
        <pre className="text-xs">{JSON.stringify(debugInfo, null, 2)}</pre>
        <p><strong>isAuthenticated:</strong> {isAuthenticated ? '✅ TRUE' : '❌ FALSE'}</p>
      </div>

      {/* Header */}
      <header className="bg-white dark:bg-night-900/85 shadow-sm border-b border-gray-100 dark:border-night-850/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="text-white w-5 h-5" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">DEBUG: Muay-Thai.app Beta</h1>
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageToggle />
              {!isAuthenticated && (
                <Button
                  onClick={() => window.location.href = '/login'}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  {t('nav.login')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

        {/* Section 1: SimpleTracking - ALWAYS SHOWN */}
        <div className="border-4 border-blue-500 p-4 mb-4">
          <h2 className="text-lg font-bold text-blue-800 mb-2">🎯 SECTION 1: SimpleTracking Component</h2>
          <p className="text-sm text-blue-600 mb-2">This component handles its own auth logic internally</p>
          <div className="mb-8">
            <SimpleTracking />
          </div>
        </div>

        {/* Section 2: Beta Info - CONDITIONAL */}
        <div className="border-4 border-green-500 p-4 mb-4">
          <h2 className="text-lg font-bold text-green-800 mb-2">🔒 SECTION 2: Beta Information (Conditional)</h2>
          <p className="text-sm text-green-600 mb-2">
            Condition: !isAuthenticated = {!isAuthenticated ? '✅ TRUE (will show)' : '❌ FALSE (will hide)'}
          </p>

          {!isAuthenticated ? (
            <div className="bg-green-50 p-4 rounded">
              <h3 className="font-bold">✅ Beta Info sections would show here</h3>
              <p>- Beta Information Section</p>
              <p>- Privacy Section</p>
              <p>- How it Works</p>
              <p>- Clear Instructions</p>
              <p>- Info Box</p>
            </div>
          ) : (
            <div className="bg-red-50 p-4 rounded">
              <h3 className="font-bold text-red-800">❌ Beta Info sections are HIDDEN</h3>
              <p className="text-red-600">User is authenticated, so these sections don't render</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 dark:bg-night-900 text-white py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">🐛 DEBUG MODE - TrainIQ Beta</p>
        </div>
      </footer>
    </div>
  )
}